import { CommonModule } from "@angular/common";
import { Component, inject, Inject } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRouteSnapshot } from "@angular/router";
import { MaterialModule } from "src/app/material.module";
import {
  eventAction,
  ViewContextHistoryStateService,
} from "src/app/shared/services/view-context-history-state.service";
import {
  extractSupportErrorMessage,
  recordApiRequestFailed,
  recordApiRequestStarted,
  recordApiRequestSucceeded,
  resolveSupportContextError,
  type SupportHistoryRecordOptions,
} from "src/app/shared/helpers/support-api-history.helper";
import { TranslateModule } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { catchError, EMPTY, finalize, tap } from "rxjs";
import { StoreService } from "src/app/services/apps/store/store.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { ClaimType } from "src/app/pages/store-management/claims/claim-model";
import { ProviderClaimTypesGetDTO } from "src/app/pages/providers/provider.model";
import {
  StoreConfigResponse,
  StoreGetDTO,
  StoreThresholdsPayload,
} from "../store";

interface DialogData {
  store: StoreGetDTO;
  storeConfig?: StoreConfigResponse;
  claimTypes: ProviderClaimTypesGetDTO[];
  parentRoute?: ActivatedRouteSnapshot;
}

@Component({
  standalone: true,
  selector: "app-store-update-threshold-dialog",
  templateUrl: "./store-update-threshold-dialog.component.html",
  styleUrls: ["./store-update-threshold-dialog.component.scss"],
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
  ],
})
export class StoreUpdateThresholdDialogComponent {
  thresholdForm: FormGroup;
  store: StoreGetDTO;
  claimTypesForProvider: ProviderClaimTypesGetDTO[] = [];

  private submitHistory = new Map<string, string>();

  private fb = inject(FormBuilder);
  private loaderSvc = inject(LoaderService);
  private storeService = inject(StoreService);
  private readonly viewContextHistorySvc = inject(
    ViewContextHistoryStateService<Record<string, unknown>>,
  );
  private readonly parentRoute = this.data.parentRoute;

  constructor(
    public dialogRef: MatDialogRef<StoreUpdateThresholdDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.store = { ...data.store };
    this.claimTypesForProvider = data.claimTypes.filter(
      (claimType) =>
        claimType.provider === this.store.provider &&
        claimType.value !== ClaimType.Unknown,
    );

    this.thresholdForm = this.fb.group({});
    this.claimTypesForProvider.forEach((claimType) => {
      this.thresholdForm.addControl(
        claimType.value,
        this.fb.group({
          thresholdAmount: [0, [Validators.required, Validators.min(0)]],
          thresholdPercentage: [
            0,
            [Validators.required, Validators.min(0), Validators.max(100)],
          ],
        }),
      );
    });

    this.patchThresholds(data.storeConfig?.thresholds);

    if (this.hasStoredThresholdConfig(data.storeConfig)) {
      this.cacheFormValue("threshold", this.thresholdForm.value);
    }
  }

  get isThresholdFormCached(): boolean {
    return this.isFormValueCached("threshold", this.thresholdForm);
  }

  submitThreshold(): void {
    if (this.thresholdForm.invalid) {
      return;
    }

    const thresholds = this.claimTypesForProvider.reduce(
      (acc: StoreThresholdsPayload, claimType) => {
        const group = this.thresholdForm.get(claimType.value) as FormGroup;
        acc[claimType.value] = {
          thresholdAmount: group.get("thresholdAmount")?.value,
          thresholdPercentage: group.get("thresholdPercentage")?.value,
        };
        return acc;
      },
      {},
    );

    this.loaderSvc.show();
    this.recordSupportHistory(
      "form_submitted",
      `User submitted threshold update for storeId=${this.store.storeId}`,
    );
    recordApiRequestStarted(
      (event, description, options) => this.recordSupportHistory(event, description, options),
      `Updating threshold metadata for storeId=${this.store.storeId}`,
    );

    this.storeService
      .updateThreshold(this.store.storeId, thresholds)
      .pipe(
        tap(() => {
          recordApiRequestSucceeded(
            (event, description, options) => this.recordSupportHistory(event, description, options),
            `Threshold metadata updated for storeId=${this.store.storeId}`,
          );
          this.cacheFormValue("threshold", this.thresholdForm.value);
          this.dialogRef.close({ event: "Refresh" });
        }),
        catchError((error) => {
          console.error("Error updating thresholds:", error);
          recordApiRequestFailed(
            (event, description, options) => this.recordSupportHistory(event, description, options),
            "update store threshold metadata",
            error,
            "mutation",
            { storeId: this.store.storeId },
          );
          return EMPTY;
        }),
        finalize(() => this.loaderSvc.hide()),
      )
      .subscribe();
  }

  private recordSupportHistory(
    eventAction: eventAction,
    eventDescription: string,
    options?: SupportHistoryRecordOptions,
  ): void {
    if (!this.parentRoute) {
      return;
    }

    this.viewContextHistorySvc.createContext({
      titleView: "Store Threshold Update",
      eventAction,
      eventDescription,
      thisState: { storeId: this.store.storeId, formValue: this.thresholdForm.value },
      route: this.parentRoute,
      error: resolveSupportContextError(eventAction, options?.error),
      setAutomaticallyScreenshot: false,
    });
  }

  private patchThresholds(thresholds?: StoreThresholdsPayload): void {
    if (!thresholds) {
      return;
    }
    Object.entries(thresholds).forEach(([claimType, values]) => {
      const group = this.thresholdForm.get(claimType) as FormGroup;
      if (group) {
        group.patchValue({
          thresholdAmount: values.thresholdAmount,
          thresholdPercentage: values.thresholdPercentage,
        });
      }
    });
  }

  private hasStoredThresholdConfig(config?: StoreConfigResponse): boolean {
    return !!config && Object.keys(config.thresholds ?? {}).length > 0;
  }

  private serializeFormValue(value: unknown): string {
    return JSON.stringify(this.sortObject(value));
  }

  private sortObject(value: unknown): unknown {
    if (value === null || typeof value !== "object") {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sortObject(item));
    }

    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((sorted, key) => {
        sorted[key] = this.sortObject((value as Record<string, unknown>)[key]);
        return sorted;
      }, {});
  }

  private cacheFormValue(step: string, value: unknown): void {
    this.submitHistory.set(step, this.serializeFormValue(value));
  }

  private isFormValueCached(step: string, form: FormGroup): boolean {
    if (form.invalid) {
      return false;
    }

    const cached = this.submitHistory.get(step);
    if (!cached) {
      return false;
    }

    return cached === this.serializeFormValue(form.value);
  }
}
